import { useState } from "react";
import { api } from "../../lib/api";
import { useDashboard } from "../../context/DashboardContext";
import { toast } from "sonner";
import { Sparkles, Send, RefreshCw, TrendingDown, TrendingUp, Lightbulb, Target } from "lucide-react";
import { Button } from "../ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const TYPE_META = {
  budget_reallocation: { color: "#10B981", icon: TrendingUp, label: "Budget Reallocation" },
  creative_fatigue: { color: "#EF4444", icon: TrendingDown, label: "Creative Fatigue" },
  keyword_performance: { color: "#38BDF8", icon: Target, label: "Keyword Performance" },
  opportunity: { color: "#F59E0B", icon: Lightbulb, label: "Opportunity" },
};

function InsightCard({ ins }) {
  const meta = TYPE_META[ins.insight_type] || TYPE_META.opportunity;
  const Icon = meta.icon;
  return (
    <div data-testid="insight-card"
      className="rounded-xl bg-[#1E293B] border border-white/10 p-5 transition-transform hover:-translate-y-0.5"
      style={{ borderLeft: `3px solid ${meta.color}` }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: meta.color }} />
          <span className="text-xs uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</span>
        </div>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#0F172A] border border-white/10 text-slate-400">
          Impact {ins.impact_score}
        </span>
      </div>
      <p className="text-sm text-slate-200 leading-relaxed">{ins.summary}</p>
      <p className="text-xs text-slate-400 mt-2 leading-relaxed">
        <span className="text-slate-500 uppercase tracking-wider">Action · </span>
        {ins.recommended_action}
      </p>
    </div>
  );
}

export default function AiInsights({ insights, onRefresh }) {
  const { branchId } = useDashboard();
  const [model, setModel] = useState("claude");
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState("");
  const [asking, setAsking] = useState(false);
  const [generating, setGenerating] = useState(false);

  const ask = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setAsking(true);
    setAnswer("");
    try {
      const { data } = await api.post("/ai/query", { question: q, model, branch_id: branchId });
      setAnswer(data.answer);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Query failed");
    } finally {
      setAsking(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post("/ai/insights/generate", { model, branch_id: branchId });
      toast.success("AI insights refreshed");
      await onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const suggestions = [
    "Which platform gave the cheapest Hair Transplant leads?",
    "Summarize performance for the last 14 days",
    "Where should I reallocate budget next week?",
  ];

  return (
    <div data-testid="ai-insights" className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#38BDF8]" />
          <h2 className="font-display text-lg font-semibold">AI Strategic Advisor</h2>
        </div>
        <div className="flex items-center gap-2">
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger data-testid="ai-model-selector"
              className="h-9 w-[150px] bg-[#1E293B] border-white/10 text-slate-200 rounded-full text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1E293B] border-white/10 text-slate-200">
              <SelectItem value="claude">Claude Sonnet 4.6</SelectItem>
              <SelectItem value="gemini">Gemini 3 Flash</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="generate-insights-btn" onClick={generate} disabled={generating}
            className="h-9 bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0F172A] font-semibold rounded-full text-xs">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${generating ? "animate-spin" : ""}`} />
            {generating ? "Analyzing…" : "Generate Insights"}
          </Button>
        </div>
      </div>

      {/* Query bar */}
      <form onSubmit={ask}
        className="flex items-center gap-2 rounded-xl bg-[#1E293B] border border-white/10 px-4 py-2 focus-within:border-[#38BDF8] transition-colors">
        <Sparkles className="w-4 h-4 text-[#38BDF8] shrink-0" />
        <input data-testid="ai-query-input" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Ask anything about your marketing performance…"
          className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-slate-500" />
        <Button type="submit" data-testid="ai-query-submit" disabled={asking}
          className="h-8 w-8 p-0 bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0F172A] rounded-full">
          {asking ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>

      {!answer && !asking && (
        <div className="flex flex-wrap gap-2">
          {suggestions.map((s, i) => (
            <button key={i} data-testid={`ai-suggestion-${i}`} onClick={() => { setQ(s); }}
              className="text-xs px-3 py-1.5 rounded-full bg-[#1E293B] border border-white/10 text-slate-400 hover:text-white hover:border-[#38BDF8]/40 transition-colors">
              {s}
            </button>
          ))}
        </div>
      )}

      {(answer || asking) && (
        <div data-testid="ai-answer" className="rounded-xl bg-[#0F172A] border border-[#38BDF8]/30 p-5 glow-brand">
          <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wider text-[#38BDF8]">
            <Sparkles className="w-3.5 h-3.5" /> AI Answer
          </div>
          <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">
            {asking ? "Thinking…" : answer}
          </p>
        </div>
      )}

      {/* Insight feed */}
      {insights && insights.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {insights.map((ins) => <InsightCard key={ins.id} ins={ins} />)}
        </div>
      ) : (
        <div className="rounded-xl bg-[#1E293B] border border-dashed border-white/10 p-8 text-center">
          <Sparkles className="w-8 h-8 text-slate-600 mx-auto mb-2" />
          <p className="text-sm text-slate-400">No insights yet. Click <span className="text-[#38BDF8]">Generate Insights</span> to let AI analyze your data.</p>
        </div>
      )}
    </div>
  );
}
